import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Compass,
  GitMerge,
  Timer,
  ScanFace,
  Sun,
  Moon,

  Houses,
  Boxes,
  Landmark,
  Waypoints,
  Radar,
  Cctv,
  LayoutGrid,
  LogOut,
  TrainFront,
  BellRing,
  DoorOpen,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { logout } from '../../auth/auth';
import baseapi from '../../baseapi';

interface DockItem {
  id: string;
  label: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  defaultStyle: string; // Distinct solid background & text per item when unselected
}

export const MacDock: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme, setTheme } = useTheme();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // alerts waiting for acknowledgement, for the badge on the Alerts icon (alerts.py; 14 Oct 2026)
  const [alertCounts, setAlertCounts] = useState<{ unacked: number; critical_unacked: number } | null>(null);
  useEffect(() => {
    let alive = true;
    const load = () => baseapi.get('alerts?state=open')
      .then(r => { if (alive && r.data?.counts) setAlertCounts(r.data.counts); })
      .catch(() => { /* keep the last count */ });
    load();
    const t = window.setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => { alive = false; window.clearInterval(t); };
  }, []);

  const handleLogout = () => {
    logout();   // Keycloak sign-out on the main server, built-in session elsewhere
  };

  const navItems: DockItem[] = [
    {
      id: 'overview',
      label: 'Overview',
      path: '/overview',
      icon: Radar,
      defaultStyle: 'bg-emerald-50 text-emerald-600 border border-emerald-200/80 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 dark:hover:bg-[#173229]',
    },
    {
      id: 'operations',
      label: 'Operations',
      path: '/operations',
      icon: Compass,
      defaultStyle: 'bg-cyan-50 text-cyan-600 border border-cyan-200/80 hover:bg-cyan-100 dark:bg-[#11242e] dark:text-cyan-400 dark:border-cyan-500/20 dark:hover:bg-[#16303d]',
    },
    {
      id: 'junctions',
      label: 'Junctions',
      path: '/junctions',
      icon: GitMerge,
      defaultStyle: 'bg-amber-50 text-amber-600 border border-amber-200/80 hover:bg-amber-100 dark:bg-[#261f12] dark:text-amber-400 dark:border-amber-500/20 dark:hover:bg-[#332918]',
    },
    {
      id: 'darshan-timings',
      label: 'Darshan Timings',
      path: '/darshan-timings',
      icon: Timer,
      defaultStyle: 'bg-yellow-50 text-yellow-700 border border-yellow-200/80 hover:bg-yellow-100 dark:bg-[#26220f] dark:text-yellow-300 dark:border-yellow-500/20 dark:hover:bg-[#332d14]',
    },
    {
      id: 'darshan-evidence',
      label: 'Darshan Time Evidence',
      path: '/darshan-evidence',
      icon: ScanFace,
      defaultStyle: 'bg-teal-50 text-teal-700 border border-teal-200/80 hover:bg-teal-100 dark:bg-[#0f2624] dark:text-teal-300 dark:border-teal-500/20 dark:hover:bg-[#143330]',
    },
    {
      id: 'qlines',
      label: 'Q-Lines',
      path: '/qlines',
      icon: Waypoints,
      defaultStyle: 'bg-fuchsia-50 text-fuchsia-600 border border-fuchsia-200/80 hover:bg-fuchsia-100 dark:bg-[#2a1430] dark:text-fuchsia-400 dark:border-fuchsia-500/20 dark:hover:bg-[#361a3e]',
    },
    {
      id: 'trains',
      label: 'Trains',
      path: '/trains',
      icon: TrainFront,
      defaultStyle: 'bg-indigo-50 text-indigo-600 border border-indigo-200/80 hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:border-indigo-500/20 dark:hover:bg-[#1c1f3a]',
    },
    {
      id: 'cameras',
      label: 'Cameras',
      path: '/cameras',
      icon: Cctv,
      defaultStyle: 'bg-indigo-50 text-indigo-600 border border-indigo-200/80 hover:bg-indigo-100 dark:bg-[#181a36] dark:text-indigo-400 dark:border-indigo-500/20 dark:hover:bg-[#1f2246]',
    },
    {
      id: 'holding-area',
      label: 'Holding Area',
      path: '/holding-area',
      icon: Houses,
      defaultStyle: 'bg-green-50 text-green-600 border border-green-200/80 hover:bg-green-100 dark:bg-[#12261f] dark:text-green-400 dark:border-green-500/20 dark:hover:bg-[#173229]',
    },
    {
      id: 'sector-readiness',
      label: 'Sector Readiness',
      path: '/sector-readiness',
      icon: Boxes,
      defaultStyle: 'bg-blue-50 text-blue-600 border border-blue-200/80 hover:bg-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20 dark:hover:bg-[#17253a]',
    },
    {
      id: 'video-wall',
      label: 'Video Wall',
      path: '/video-wall',
      icon: LayoutGrid,
      defaultStyle: 'bg-violet-50 text-violet-600 border border-violet-200/80 hover:bg-violet-100 dark:bg-[#1c1836] dark:text-violet-400 dark:border-violet-500/20 dark:hover:bg-[#241f46]',
    },
    {
      id: 'main-templeroom',
      label: 'Main Temple Room',
      path: '/main-templeroom',
      icon: Landmark,
      defaultStyle: 'bg-teal-50 text-teal-600 border border-teal-200/80 hover:bg-teal-100 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20 dark:hover:bg-[#173229]',
    },
    // Digital Twin: taken off the dock on 12 Oct 2026 (the page still opens at /digital-twin)
    // {
    //   id: 'digital-twin',
    //   label: 'Digital Twin',
    //   path: '/digital-twin',
    //   icon: Boxes,
    //   defaultStyle: 'bg-cyan-50 text-cyan-600 border border-cyan-200/80 hover:bg-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20 dark:hover:bg-[#173229]',
    // },


    // {
    //   id: 'analytics',
    //   label: 'Density Analytics',
    //   path: '/analytics',
    //   icon: BarChart3,
    //   defaultStyle: 'bg-violet-50 text-violet-600 border border-violet-200/80 hover:bg-violet-100 dark:bg-[#201833] dark:text-violet-400 dark:border-violet-500/20 dark:hover:bg-[#2a1f42]',
    // },
    {
      id: 'antharalayam-cameras',
      label: 'Queue cameras',
      path: '/antharalayam-cameras',
      icon: DoorOpen,
      defaultStyle: 'bg-amber-50 text-amber-700 border border-amber-200/80 hover:bg-amber-100 dark:bg-[#2a2113] dark:text-amber-400 dark:border-amber-500/20 dark:hover:bg-[#35291a]',
    },
    {
      id: 'alerts',
      label: 'Alerts',
      path: '/alerts',
      icon: BellRing,
      defaultStyle: 'bg-rose-50 text-rose-600 border border-rose-200/80 hover:bg-rose-100 dark:bg-[#2c151c] dark:text-rose-400 dark:border-rose-500/20 dark:hover:bg-[#381a24]',
    },
    // {
    //   id: 'settings',
    //   label: 'System Config',
    //   path: '/settings',
    //   icon: Sliders,
    //   defaultStyle: 'bg-zinc-100 text-zinc-700 border border-zinc-200/80 hover:bg-zinc-200 dark:bg-[#1f232c] dark:text-zinc-300 dark:border-zinc-700/30 dark:hover:bg-[#262c37]',
    // },
  ];

  return (
    <nav
      aria-label="macOS Left Navigation Dock"
      className="fixed left-2.5 top-[calc(var(--topbar-total-h,92px)+12px)] bottom-3 z-50 select-none pointer-events-none flex flex-col justify-between items-center w-10 transition-all duration-200"
    >
      {/* 1. TOP OF SCREEN: Dark & Light Mode Sliding Toggle Switch Pill */}
      <div className="pointer-events-auto p-1 rounded-full bg-white/95 dark:bg-[#14161e]/95 backdrop-blur-md border border-neutral-200/90 dark:border-[#272d3d] shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-200 flex flex-col items-center justify-center">
        <div
          className="relative group flex items-center justify-center"
          onMouseEnter={() => setHoveredId('theme-toggle')}
          onMouseLeave={() => setHoveredId(null)}
        >
          {/* Tooltip to the right */}
          <div
            className={`absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide whitespace-nowrap pointer-events-none transition-all duration-150 shadow-md z-50 ${hoveredId === 'theme-toggle'
              ? 'opacity-100 translate-x-0 scale-100'
              : 'opacity-0 -translate-x-1 scale-95'
              } bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white border border-neutral-200 dark:border-neutral-800`}
          >
            {theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          </div>

          {/* Vertical Toggle Switch with Pixel-Perfect Alignment */}
          <div
            onClick={toggleTheme}
            role="button"
            aria-label="Toggle Dark / Light Mode"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleTheme(); }}
            className="relative w-[28px] h-[52px] rounded-full p-[3px] flex flex-col justify-between items-center bg-neutral-100 dark:bg-[#181b23] border border-neutral-200/90 dark:border-[#272e3d] shadow-inner cursor-pointer transition-colors duration-200 select-none box-border"
          >
            {/* Sliding Pill Thumb */}
            <span
              className={`absolute top-[2px] left-[2px] w-[22px] h-[22px] rounded-full bg-white dark:bg-[#0c0f16] shadow-[0_2px_6px_rgba(0,0,0,0.16)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.6)] border border-neutral-200/70 dark:border-zinc-700/60 transition-transform duration-200 ease-out pointer-events-none ${theme === 'dark' ? 'translate-y-[24px]' : 'translate-y-0'
                }`}
            />

            {/* Sun Slot (Top / Light) */}
            <div
              onClick={(e) => { e.stopPropagation(); setTheme('light'); }}
              className={`relative z-10 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-all duration-200 ${theme === 'light' ? 'text-amber-500 scale-105' : 'text-neutral-400 dark:text-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 opacity-60'
                }`}
            >
              <Sun className="w-3 h-3" />
            </div>

            {/* Moon Slot (Bottom / Dark) */}
            <div
              onClick={(e) => { e.stopPropagation(); setTheme('dark'); }}
              className={`relative z-10 w-[22px] h-[22px] rounded-full flex items-center justify-center transition-all duration-200 ${theme === 'dark' ? 'text-blue-400 scale-105' : 'text-neutral-400 hover:text-neutral-600 opacity-60'
                }`}
            >
              <Moon className="w-3 h-3" />
            </div>
          </div>
        </div>
      </div>

      {/* 2. MIDDLE OF SCREEN: Mac Dock Navigation Pill */}
      <div className="pointer-events-auto my-auto flex flex-col items-center gap-1 p-1 rounded-full bg-white/95 dark:bg-[#14161e]/95 backdrop-blur-md border border-neutral-200/90 dark:border-[#272d3d] shadow-[0_10px_35px_rgba(0,0,0,0.1)] dark:shadow-[0_16px_50px_rgba(0,0,0,0.5)] transition-all duration-200">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const isHovered = hoveredId === item.id;
          const Icon = item.icon;

          return (
            <div
              key={item.id}
              className="relative group flex items-center justify-center"
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {/* macOS Tooltip (positioned to the right of the left dock) */}
              <div
                className={`absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide whitespace-nowrap pointer-events-none transition-all duration-150 shadow-md z-50 ${isHovered
                  ? 'opacity-100 translate-x-0 scale-100'
                  : 'opacity-0 -translate-x-1 scale-95'
                  } bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white border border-neutral-200 dark:border-neutral-800`}
              >
                {item.label}
              </div>

              {/* Dock Icon Button: Circular */}
              <button
                onClick={() => navigate(item.path)}
                className={`relative w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-150 cursor-pointer ${isActive
                  ? 'bg-blue-500 text-white border border-blue-400 ring-1 ring-blue-500/25'
                  : item.defaultStyle
                  }`}
              >
                <Icon className="w-4 h-4" />
                {item.id === 'alerts' && !!alertCounts?.unacked && (
                  <span title={`${alertCounts.unacked} alert(s) waiting for acknowledgement`}
                    className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-black leading-4 text-center text-white ${alertCounts.critical_unacked ? 'bg-[#BE123C] animate-pulse' : 'bg-amber-500'}`}>
                    {alertCounts.unacked > 99 ? '99+' : alertCounts.unacked}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </div>

      {/* 3. BOTTOM OF SCREEN: Logout */}
      <div className="pointer-events-auto flex flex-col items-center gap-1 p-1 rounded-full bg-white/95 dark:bg-[#14161e]/95 backdrop-blur-md border border-neutral-200/90 dark:border-[#272d3d] shadow-[0_4px_16px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)] transition-all duration-200">
        {/* Logout Button above OP */}
        <div
          className="relative group flex items-center justify-center"
          onMouseEnter={() => setHoveredId('logout-btn')}
          onMouseLeave={() => setHoveredId(null)}
        >
          <div
            className={`absolute left-full ml-3 top-1/2 -translate-y-1/2 px-2.5 py-1 rounded-md text-[11px] font-semibold tracking-wide whitespace-nowrap pointer-events-none transition-all duration-150 shadow-md z-50 ${hoveredId === 'logout-btn'
              ? 'opacity-100 translate-x-0 scale-100'
              : 'opacity-0 -translate-x-1 scale-95'
              } bg-neutral-100 text-neutral-900 dark:bg-neutral-800 dark:text-white border border-neutral-200 dark:border-neutral-800`}
          >
            Logout
          </div>

          <button
            onClick={handleLogout}
            title="Logout"
            aria-label="Logout"
            className="relative w-8 h-8 rounded-full flex items-center justify-center transition-all duration-150 cursor-pointer bg-red-50 text-red-600 border border-red-200/80 hover:bg-red-100 hover:text-red-700 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20 dark:hover:bg-red-500/20 dark:hover:text-red-300 hover:scale-105"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </nav>
  );
};
